import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Shield, Activity, RefreshCw, Wifi, WifiOff, AlertTriangle,
  CheckCircle, XCircle, HelpCircle, Zap, Server, Link2, Users,
  Database, Clock, Loader2, Play, ChevronDown, ChevronUp, Settings,
  ArrowRight, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatInCST } from '../utils/timezone';

/* ── Status visual config ──────────────────────────────────────────── */
const STATUS_STYLE = {
  HEALTHY:      { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500', icon: CheckCircle },
  NEEDS_ATTENTION: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500', icon: AlertTriangle },
  DEGRADED:     { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500', icon: AlertTriangle },
  ERROR:        { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500', icon: XCircle },
  DISCONNECTED: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500', icon: WifiOff },
  UNKNOWN:      { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-500', dot: 'bg-slate-400', icon: HelpCircle },
  DISABLED:     { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-400', dot: 'bg-gray-300', icon: Shield },
};

function StatusDot({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.UNKNOWN;
  return <span className={`inline-block w-2 h-2 rounded-full ${s.dot}`} />;
}

function StatusLabel({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.UNKNOWN;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${s.bg} ${s.text} border ${s.border}`}>
      <StatusDot status={status} />
      {status === 'NEEDS_ATTENTION' ? 'Needs Attention' : status === 'DISCONNECTED' ? 'Disconnected' : status}
    </span>
  );
}

function formatAgo(isoStr) {
  if (!isoStr) return 'Never';
  try {
    const d = new Date(isoStr);
    const mins = Math.floor((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  } catch { return '—'; }
}

/* ── Health group definitions ────────────────────────────────────── */
const HEALTH_GROUPS = [
  {
    key: 'connection', label: 'Connection', icon: Wifi,
    subsystems: ['api', 'webhooks'],
  },
  {
    key: 'synchronization', label: 'Synchronization', icon: RefreshCw,
    subsystems: ['incremental_sync', 'reconciliation', 'inventory_freshness'],
  },
  {
    key: 'configuration', label: 'Configuration', icon: Settings,
    subsystems: ['mappings', 'device_mapping', 'identity_rbac'],
  },
  {
    key: 'automation', label: 'Automation', icon: Zap,
    subsystems: ['automation_delivery'],
  },
];

const SUBSYSTEM_LABELS = {
  api: 'API / Authentication',
  webhooks: 'Webhooks',
  incremental_sync: 'Incremental Sync',
  reconciliation: 'Full Reconciliation',
  inventory_freshness: 'Inventory Freshness',
  mappings: 'Organization Mapping',
  device_mapping: 'Device Mapping',
  identity_rbac: 'Identity / RBAC',
  automation_delivery: 'Automation Delivery',
};

const SUBSYSTEM_ICONS = {
  api: Wifi, webhooks: Zap, incremental_sync: RefreshCw,
  reconciliation: Database, inventory_freshness: Clock,
  mappings: Link2, device_mapping: Server, identity_rbac: Users,
  automation_delivery: Activity,
};

/* ── Compute top-level display status ────────────────────────────── */
function computeDisplayStatus(health) {
  if (!health) return { label: 'UNKNOWN', status: 'UNKNOWN' };
  const raw = health.overall_status;
  // If only issue is mapping (not a provider failure), show CONNECTED + NEEDS_ATTENTION
  const hasRealError = (health.issues || []).some(i =>
    i.subsystem === 'api' && (i.severity === 'ERROR' || i.severity === 'DISCONNECTED')
  );
  if (raw === 'DISCONNECTED' || hasRealError) return { label: 'DISCONNECTED', status: 'DISCONNECTED' };
  if (raw === 'ERROR') return { label: 'ERROR', status: 'ERROR' };
  if (raw === 'DEGRADED') return { label: 'CONNECTED', status: 'NEEDS_ATTENTION' };
  if (raw === 'HEALTHY') return { label: 'CONNECTED', status: 'HEALTHY' };
  return { label: raw, status: raw };
}

/* ── Main Component ──────────────────────────────────────────────── */
export default function RmmHealthDashboard({ onNavigateMapping }) {
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconRunning, setReconRunning] = useState(false);
  const [testingConn, setTestingConn] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);

  const loadHealth = useCallback(async () => {
    try {
      const r = await api.get('/rmm/health');
      setHealth(r.data);
    } catch (err) {
      if (err.response?.status !== 403) toast.error('Failed to load integration health');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadHealth();
    const interval = setInterval(loadHealth, 45000);
    return () => clearInterval(interval);
  }, [loadHealth]);

  const triggerReconciliation = async () => {
    setReconRunning(true);
    try {
      await api.post('/rmm/reconciliation/full');
      toast.success('Full reconciliation started');
      setTimeout(loadHealth, 5000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to start reconciliation');
    }
    setReconRunning(false);
  };

  const testConnection = async () => {
    setTestingConn(true);
    try {
      const r = await api.post('/rmm/health/test-connection');
      if (r.data.status === 'connected') {
        toast.success(`Connected (${r.data.latency_ms}ms)`);
        loadHealth();
      } else {
        toast.error(r.data.error || 'Connection failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Connection test failed');
    }
    setTestingConn(false);
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>;
  if (!health) return null;

  const display = computeDisplayStatus(health);
  const displayStyle = STATUS_STYLE[display.status] || STATUS_STYLE.UNKNOWN;
  const DisplayIcon = displayStyle.icon;
  const m = health.metrics || {};

  // Compute group-level worst status
  function groupWorst(subsystemKeys) {
    const prio = { DISCONNECTED: 5, ERROR: 4, DEGRADED: 3, UNKNOWN: 2, HEALTHY: 1, DISABLED: 0 };
    let worst = 'HEALTHY';
    for (const k of subsystemKeys) {
      const s = health.subsystems?.[k]?.status || 'UNKNOWN';
      if (s === 'DISABLED') continue;
      if ((prio[s] || 0) > (prio[worst] || 0)) worst = s;
    }
    return worst;
  }

  // Count issues needing action (exclude DISABLED/UNKNOWN)
  const actionableIssues = (health.issues || []).filter(i => i.severity !== 'DISABLED' && i.severity !== 'UNKNOWN');
  const mappingIssue = actionableIssues.find(i => i.code === 'RMM_UNMAPPED_ORGS');

  return (
    <div className="space-y-3" data-testid="rmm-health-dashboard">
      {/* ── N1.6: Health Header ──────────────────────────────────── */}
      <Card className={`border-l-4 ${display.status === 'HEALTHY' ? 'border-l-emerald-500' : display.status === 'NEEDS_ATTENTION' ? 'border-l-amber-500' : display.status === 'DISCONNECTED' || display.status === 'ERROR' ? 'border-l-red-500' : 'border-l-slate-400'}`}>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {/* Left: Status */}
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${displayStyle.bg}`}>
                <Server className={`h-6 w-6 ${displayStyle.text}`} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-lg text-gray-900">NinjaOne</h3>
                  <StatusLabel status={display.status} />
                </div>
                {actionableIssues.length > 0 && (
                  <p className="text-sm text-gray-500 mt-0.5">
                    {actionableIssues.length === 1
                      ? actionableIssues[0].message
                      : `${actionableIssues.length} items need attention`}
                  </p>
                )}
                {actionableIssues.length === 0 && display.status === 'HEALTHY' && (
                  <p className="text-sm text-gray-500 mt-0.5">All systems operating normally</p>
                )}
              </div>
            </div>

            {/* Right: Key timestamps + Actions */}
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={testConnection} disabled={testingConn} data-testid="health-test-connection">
                {testingConn ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Wifi className="h-3.5 w-3.5 mr-1.5" />}
                Test Connection
              </Button>
              <Button variant="outline" size="sm" onClick={triggerReconciliation} disabled={reconRunning} data-testid="health-run-reconciliation">
                {reconRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                Reconcile
              </Button>
              {mappingIssue && (
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={() => onNavigateMapping ? onNavigateMapping() : navigate('/integrations')}
                  data-testid="health-resolve-mappings"
                >
                  <Link2 className="h-3.5 w-3.5 mr-1.5" />
                  Resolve Mappings
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={loadHealth} data-testid="health-refresh">
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Key operational timestamps */}
          <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 pt-3 border-t text-xs text-gray-500">
            <span data-testid="health-ts-api">Last API: <strong className="text-gray-700">{formatAgo(m.last_api_success)}</strong></span>
            <span data-testid="health-ts-recon">Last Reconciliation: <strong className="text-gray-700">{formatAgo(m.last_reconciliation)}</strong></span>
            <span data-testid="health-ts-sync">Last Incremental Sync: <strong className="text-gray-700">{formatAgo(m.last_incremental_sync)}</strong></span>
            {m.total_devices != null && <span>Devices: <strong className="text-gray-700">{m.total_devices}</strong></span>}
            {m.mapped_organizations != null && m.provider_organizations != null && (
              <span>Orgs Mapped: <strong className="text-gray-700">{m.mapped_organizations}/{m.provider_organizations}</strong></span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── N1.7: Grouped Health Subsystems ──────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {HEALTH_GROUPS.map(group => {
          const GroupIcon = group.icon;
          const gStatus = groupWorst(group.subsystems);
          const gStyle = STATUS_STYLE[gStatus] || STATUS_STYLE.UNKNOWN;
          const isExpanded = expandedGroup === group.key;
          const subsInGroup = group.subsystems.filter(k => health.subsystems?.[k]);

          return (
            <Card
              key={group.key}
              className={`cursor-pointer transition-shadow hover:shadow-md ${isExpanded ? 'ring-1 ring-gray-300' : ''}`}
              onClick={() => setExpandedGroup(isExpanded ? null : group.key)}
              data-testid={`health-group-${group.key}`}
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <GroupIcon className={`h-4 w-4 ${gStyle.text}`} />
                    <span className="text-sm font-medium text-gray-900">{group.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusLabel status={gStatus} />
                    {isExpanded ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
                  </div>
                </div>

                {isExpanded && subsInGroup.length > 0 && (
                  <div className="mt-3 pt-2 border-t space-y-2">
                    {subsInGroup.map(sKey => {
                      const sub = health.subsystems[sKey];
                      const SubIcon = SUBSYSTEM_ICONS[sKey] || Activity;
                      const sStyle = STATUS_STYLE[sub.status] || STATUS_STYLE.UNKNOWN;
                      return (
                        <div key={sKey} className={`flex items-start gap-2 p-2 rounded ${sStyle.bg} border ${sStyle.border}`} data-testid={`health-sub-${sKey}`}>
                          <SubIcon className={`h-3.5 w-3.5 mt-0.5 ${sStyle.text}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-gray-800">{SUBSYSTEM_LABELS[sKey] || sKey}</span>
                              <StatusDot status={sub.status} />
                            </div>
                            <p className="text-[11px] text-gray-500 mt-0.5">{sub.reason}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
